/**
 * useQuizQuestionSignal — le crochet du signalement d'une question du quiz de prérequis : la machine
 * (`quizQuestionSignal`) branchée sur l'API et les mesures de l'ancien `QuizQuestionSignalButton` :
 *  - l'ouverture : `quiz.question.signal.button_click` (`context`, `question_id`) ;
 *  - l'envoi : `POST api/v1/activities/{id}/quiz/signal` (`quiz_question_id`, `signal_type`,
 *    `context: "during_quiz"`, `comment`), puis `quiz.question.signal.submitted` (`signal_type`,
 *    `context`, `question_id`, `comment`) ;
 *  - un fil d'Ariane à chaque changement d'état, une erreur typée à chaque échec.
 */
import * as amplitude from "@amplitude/analytics-react-native"
import { useCallback, useMemo } from "react"
import { useDispatch } from "react-redux"

import { activitiesService } from "../../../data/services/activitiesService"
import { apiCall, type ApiService } from "../../shared/apiCall"
import { createTelemetry, type EngineTelemetry } from "../../shared/telemetry"
import { type PhaseTransition, useSignalReducer, withPhaseTransitions } from "../../shared/useSignalReducer"
import { INITIAL_QUIZ_SIGNAL, quizSignalReducer, type QuizSignalState, type QuizSignalType } from "./quizQuestionSignal"

const CONTEXT = "during_quiz"

export type QuizSignalService = (
    activityId: number,
    body: { quiz_question_id: number; signal_type: QuizSignalType; context: string; comment: string }
) => Promise<unknown>

export type UseQuizQuestionSignalOptions = {
    activityId: number | null
    /** La question en cours. */
    questionId: number | null
    send?: QuizSignalService
    telemetry?: EngineTelemetry
}

const phaseOf = (state: QuizSignalState) => (state.sending ? "sending" : state.open ? "open" : "closed")

const reduceWithTransitions = withPhaseTransitions(quizSignalReducer, phaseOf)

export function useQuizQuestionSignal(options: UseQuizQuestionSignalOptions) {
    const { activityId, questionId } = options
    const dispatchRedux = useDispatch() as unknown as (action: never) => unknown
    const telemetry = useMemo(
        () => options.telemetry ?? createTelemetry("activity.quiz_signal", { role: "student" }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    )
    const send = useMemo<QuizSignalService>(
        () =>
            options.send ??
            ((id, body) =>
                apiCall(dispatchRedux, activitiesService.signalQuizQuestion as ApiService, {
                    routeParams: { activityId: id },
                    data: body,
                })),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dispatchRedux]
    )
    const [state, dispatch, read] = useSignalReducer(
        reduceWithTransitions,
        () => INITIAL_QUIZ_SIGNAL,
        (transitions: PhaseTransition[]) => {
            for (const { from, to, event } of transitions) telemetry.transition(from, to, event)
        }
    )

    const open = useCallback(() => {
        amplitude.track("quiz.question.signal.button_click", { context: CONTEXT, question_id: questionId })
        dispatch({ type: "opened" })
    }, [dispatch, questionId])

    const submit = useCallback(() => {
        const current = read()
        if (current.type == null || current.sending || activityId == null || questionId == null) return
        const { type, comment } = current
        dispatch({ type: "sendStarted" })
        send(activityId, { quiz_question_id: questionId, signal_type: type, context: CONTEXT, comment })
            .then(() => {
                amplitude.track("quiz.question.signal.submitted", {
                    signal_type: type,
                    context: CONTEXT,
                    question_id: questionId,
                    comment,
                })
                dispatch({ type: "sent" })
            })
            .catch((error: unknown) => {
                telemetry.error("send", error, { signalType: type })
                dispatch({ type: "sendFailed" })
            })
    }, [activityId, dispatch, questionId, read, send, telemetry])

    return {
        state,
        open,
        close: useCallback(() => dispatch({ type: "closed" }), [dispatch]),
        setType: useCallback((signal: QuizSignalType) => dispatch({ type: "typeChosen", signal }), [dispatch]),
        setComment: useCallback((comment: string) => dispatch({ type: "commentChanged", comment }), [dispatch]),
        send: submit,
        dismissNotice: useCallback(() => dispatch({ type: "noticeDismissed" }), [dispatch]),
    }
}
